/**
 * Admin Dynamic Results Page
 *
 * Combined view of System Burden (DCF) and Leadership Burden (LPFF) results.
 * Allows real-time algorithm testing with database data.
 * Algorithm v7.1
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/Admin/AdminLayout';
import {
  AVERAGE_LOSS_RATE,
  AVERAGE_CAPTURE_RATE,
  MAX_RECOVERY_RATE,
  SYSTEM_LOSS_WEIGHT,
  LEADERSHIP_LOSS_WEIGHT,
  ACT_COACHING_MULTIPLIER,
  SUCCESSFUL_CEILING,
  DEPENDENCY_WEIGHTS,
  calculateSimplifiedResults
} from '../utils/algorithms';

// Dependency and Anxiety descriptions for tooltips
const DEPENDENCY_DESCRIPTIONS = [
  'Strategic clarity and value case: Clear transformation objectives with compelling business case and measurable outcomes.',
  'Portfolio governance and decision rights: Clear accountability, prioritisation mechanisms, and resource allocation authority.',
  'Risk and adaptive planning maturity: Proactive risk identification, scenario planning, and agile response capability.',
  'Delivery flow and interdependency management: Smooth handoffs, managed dependencies, and predictable delivery cadence.',
  'Operational performance management and data quality: Reliable metrics, data-driven decisions, and performance visibility.',
  'Change implementation and BA/DM capability: Effective change execution with strong business analysis and delivery management.',
  'Stakeholder network and sponsorship strength: Active executive sponsorship, engaged stakeholders, and coalition support.',
  'Leadership and team dynamics: Cohesive leadership team, psychological safety, and collaborative working relationships.',
  'Culture, communication and narrative truth: Honest dialogue, aligned messaging, and authentic organisational narrative.',
  'Hidden work and dependence load: Invisible coordination burden, untracked dependencies, and key person reliance.',
  'Organisational complexity and dependency density: Structural simplicity, clear reporting lines, and manageable interfaces.',
  'Transformation fatigue and history: Organisational energy for change, past transformation trauma, and change capacity.',
  'Meeting and cadence architecture: Effective meeting rhythms, decision forums, and collaborative time structures.',
  'Talent availability and critical role stability: Right people in right roles, succession planning, and retention.'
];

const ANXIETY_DESCRIPTIONS = [
  'Imposter Complex: Persistent self-doubt despite evidence of competence. Feeling like a fraud waiting to be exposed.',
  'Fragile Authority: Uncertainty about leadership legitimacy. Feeling authority could be challenged or withdrawn at any moment.',
  'Over-Dependence: Excessive reliance on others for validation, decisions, or emotional support. Difficulty acting autonomously.',
  'Moral Injury: Psychological distress from acting against deeply held values. Compromising integrity for organisational demands.',
  'Invisible Labour: Unrecognised emotional and cognitive work. The hidden burden of managing relationships, politics, and complexity.',
  'Failing the Future: Anxiety about not preparing the organisation or team for future challenges. Fear of leaving a negative legacy.',
  'Team Disengagement: Concern about team motivation, commitment, and connection. Sensing withdrawal or apathy in others.',
  'Personal Depletion: Physical and emotional exhaustion. Running on empty with insufficient recovery time or energy reserves.',
  'Identity Drift: Losing connection with personal values and sense of self. Becoming someone you don\'t recognise or respect.',
  'Loss of Meaning: Work feeling pointless or disconnected from purpose. Questioning why the effort matters anymore.'
];

const DEPENDENCY_LABELS = [
  'Strategic Clarity',
  'Portfolio Governance',
  'Risk & Adaptive Planning',
  'Delivery Flow',
  'Performance Management',
  'Change Implementation',
  'Stakeholder Network',
  'Leadership & Team Dynamics',
  'Culture & Communication',
  'Hidden Work Load',
  'Org Complexity',
  'Transformation Fatigue',
  'Meeting Architecture',
  'Talent Availability'
];

const ANXIETY_LABELS = [
  'Imposter Complex',
  'Fragile Authority',
  'Over-Dependence',
  'Moral Injury',
  'Invisible Labour',
  'Failing the Future',
  'Team Disengagement',
  'Personal Depletion',
  'Identity Drift',
  'Loss of Meaning'
];

function AdminDynamicResults() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [selectedLeaderId, setSelectedLeaderId] = useState('');
  const [showCalculations, setShowCalculations] = useState(true);
  const [showTop3, setShowTop3] = useState(true);
  const [showNextSteps, setShowNextSteps] = useState(true);
  
  // Separate diagnostic selections for System Burden and Leadership Burden
  const [systemBurdenDiagnostics, setSystemBurdenDiagnostics] = useState([]);
  const [leadershipBurdenDiagnostics, setLeadershipBurdenDiagnostics] = useState([]);
  const [selectedSystemDiagnosticId, setSelectedSystemDiagnosticId] = useState('');
  const [selectedLeadershipDiagnosticId, setSelectedLeadershipDiagnosticId] = useState('');

  // Scenarios state
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState('');

  // Save scenario modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [newScenarioDescription, setNewScenarioDescription] = useState('');
  const [saveForLeader, setSaveForLeader] = useState(false);
  const [saving, setSaving] = useState(false);

  // Data state
  const [valueAtStake, setValueAtStake] = useState(15);
  // Default dependency scores to 7 so DCF = 0.70 and DCF Normalized = 0
  const [dependencyScores, setDependencyScores] = useState(
    Array(14).fill(7).reduce((acc, val, i) => ({ ...acc, [`dep${i + 1}`]: val }), {})
  );
  const [anxietyScores, setAnxietyScores] = useState(
    Array(10).fill(5).reduce((acc, val, i) => ({ ...acc, [`anxiety${i + 1}`]: val }), {})
  );
  const [personaId, setPersonaId] = useState('mark');

  // Framework data from API (dependencies and anxieties with full details)
  const [frameworkData, setFrameworkData] = useState({ dependencies: [], anxieties: [] });

  // Fetch leaders and framework data on mount
  useEffect(() => {
    const fetchLeaders = async () => {
      try {
        const response = await fetch('/api/admin/leaders-with-diagnostics', {
          credentials: 'include'
        });

        if (response.status === 401) {
          await logout();
          navigate('/admin/login');
          return;
        }

        const data = await response.json();
        if (data.success) {
          setLeaders(data.leaders || []);
        } else {
          setError(data.error || 'Failed to load leaders');
        }
      } catch (err) {
        console.error('Leaders fetch error:', err);
        setError('Failed to load leaders');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaders();

    // Fetch framework data (dependencies and anxieties with full details)
    const fetchFrameworkData = async () => {
      try {
        const response = await fetch('/api/admin/framework-data', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setFrameworkData({
              dependencies: data.dependencies || [],
              anxieties: data.anxieties || []
            });
          }
        }
      } catch (err) {
        console.error('Framework data fetch error:', err);
      }
    };
    fetchFrameworkData();

    // Fetch generic scenarios (no leader selected yet)
    const fetchGenericScenarios = async () => {
      try {
        const response = await fetch('/api/admin/scenarios', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setScenarios(data.scenarios || []);
          }
        }
      } catch (err) {
        console.error('Scenarios fetch error:', err);
      }
    };
    fetchGenericScenarios();
  }, [navigate, logout]);

  // Fetch diagnostics and scenarios when leader changes
  useEffect(() => {
    if (!selectedLeaderId) {
      setSystemBurdenDiagnostics([]);
      setLeadershipBurdenDiagnostics([]);
      setSelectedSystemDiagnosticId('');
      setSelectedLeadershipDiagnosticId('');
      setSelectedScenarioId('');
      // Fetch generic scenarios only when no leader selected
      const fetchGenericScenarios = async () => {
        try {
          const response = await fetch('/api/admin/scenarios', {
            credentials: 'include'
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setScenarios(data.scenarios || []);
            }
          }
        } catch (err) {
          console.error('Scenarios fetch error:', err);
        }
      };
      fetchGenericScenarios();
      return;
    }

    const fetchDiagnostics = async () => {
      try {
        const response = await fetch(`/api/admin/leader-diagnostics/${selectedLeaderId}`, {
          credentials: 'include'
        });

        if (response.status === 401) {
          await logout();
          navigate('/admin/login');
          return;
        }

        const data = await response.json();
        if (data.success) {
          // Store System Burden diagnostics separately
          const systemDiags = (data.diagnostics?.systemBurden || []).map(diag => ({
            ...diag,
            type: 'system',
            displayType: 'System Burden'
          })).sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

          // Store Leadership Burden diagnostics separately
          const leadershipDiags = (data.diagnostics?.leadershipBurden || []).map(diag => ({
            ...diag,
            type: 'leadership',
            displayType: 'Leadership Burden'
          })).sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

          setSystemBurdenDiagnostics(systemDiags);
          setLeadershipBurdenDiagnostics(leadershipDiags);

          // Auto-select the most recent of each type if available
          if (systemDiags.length > 0) {
            setSelectedSystemDiagnosticId(systemDiags[0].id.toString());
          }
          if (leadershipDiags.length > 0) {
            setSelectedLeadershipDiagnosticId(leadershipDiags[0].id.toString());
          }

          // Set value at stake from most recent diagnostic (prefer system burden)
          const mostRecent = systemDiags[0] || leadershipDiags[0];
          if (mostRecent?.valueAtStake) {
            setValueAtStake(mostRecent.valueAtStake);
          }
        }
      } catch (err) {
        console.error('Diagnostics fetch error:', err);
      }
    };

    fetchDiagnostics();

    // Fetch scenarios for this leader (leader-specific + generic)
    const fetchScenarios = async () => {
      try {
        const response = await fetch(`/api/admin/scenarios?leaderId=${selectedLeaderId}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setScenarios(data.scenarios || []);
            setSelectedScenarioId('');
          }
        }
      } catch (err) {
        console.error('Scenarios fetch error:', err);
      }
    };
    fetchScenarios();
  }, [selectedLeaderId, navigate, logout]);

  // Load System Burden scores when selected
  useEffect(() => {
    if (!selectedSystemDiagnosticId || !systemBurdenDiagnostics.length) return;

    const diagnostic = systemBurdenDiagnostics.find(d => d.id === parseInt(selectedSystemDiagnosticId));
    if (!diagnostic) return;

    // Load dependency scores from diagnostic.scores object
    const newDepScores = {};
    for (let i = 1; i <= 14; i++) {
      const score = diagnostic.scores?.[`dep${i}`];
      newDepScores[`dep${i}`] = score !== null && score !== undefined ? score : 5;
    }
    setDependencyScores(newDepScores);

    // Update value at stake if this diagnostic has one
    if (diagnostic.valueAtStake) {
      setValueAtStake(diagnostic.valueAtStake);
    }
  }, [selectedSystemDiagnosticId, systemBurdenDiagnostics]);

  // Load Leadership Burden scores when selected
  useEffect(() => {
    if (!selectedLeadershipDiagnosticId || !leadershipBurdenDiagnostics.length) return;

    const diagnostic = leadershipBurdenDiagnostics.find(d => d.id === parseInt(selectedLeadershipDiagnosticId));
    if (!diagnostic) return;

    // Load anxiety scores from diagnostic.scores object
    const newAnxScores = {};
    for (let i = 1; i <= 10; i++) {
      const score = diagnostic.scores?.[`anx${i}`];
      newAnxScores[`anxiety${i}`] = score !== null && score !== undefined ? score : 5;
    }
    setAnxietyScores(newAnxScores);

    // Update value at stake if this diagnostic has one (and no system burden selected)
    if (diagnostic.valueAtStake && !selectedSystemDiagnosticId) {
      setValueAtStake(diagnostic.valueAtStake);
    }
  }, [selectedLeadershipDiagnosticId, leadershipBurdenDiagnostics, selectedSystemDiagnosticId]);

  // Calculate all algorithm values
  const calculateResults = useCallback(() => {
    // Step 1: Total Loss (McKinsey 43%)
    const totalLoss = valueAtStake * AVERAGE_LOSS_RATE;

    // Step 2: Attribution pools
    const systemLossPool = totalLoss * SYSTEM_LOSS_WEIGHT;
    const leadershipLossPool = totalLoss * LEADERSHIP_LOSS_WEIGHT;

    // Step 3: Calculate DCF using weighted formula (Algorithm v7.0)
    // DCF = Σ(weight[i] × score[i]) / (totalWeight × 10)
    let totalWeightedContribution = 0;
    let totalWeight = 0;
    DEPENDENCY_WEIGHTS.forEach(dep => {
      const score = dependencyScores[`dep${dep.id}`] || 0;
      totalWeightedContribution += dep.weight * score;
      totalWeight += dep.weight;
    });
    const rawDcf = totalWeightedContribution / (totalWeight * 10); // totalWeight = 113, so divisor = 1130

    // Step 4: Calculate LPFF (direct scores - sliders represent flexibility, not anxiety)
    const anxValues = Object.values(anxietyScores);
    const sumScores = anxValues.reduce((sum, score) => sum + score, 0);
    const rawLpff = sumScores / 100;

    // Step 5: Get persona baselines
    const personaBaselines = {
      mark: { dcf: 0.70, lpff: 0.55 },
      sarah: { dcf: 0.65, lpff: 0.65 }
    };
    const baseline = personaBaselines[personaId] || personaBaselines.mark;

    // Step 6: Use raw calculated values (no blending)
    // DCF comes from dependency sliders, LPFF comes from anxiety sliders
    const dcf = rawDcf;
    const lpff = rawLpff;

    // Step 7: Calculate CRF using documented formula
    // systemComponent = dcf × 0.685
    // leadershipComponent = lpff × 0.315 × 1.08
    const systemComponent = dcf * SYSTEM_LOSS_WEIGHT;
    const leadershipComponent = lpff * LEADERSHIP_LOSS_WEIGHT * ACT_COACHING_MULTIPLIER;
    const crf = systemComponent + leadershipComponent;

    // Step 8: Calculate recoverable value
    const recoverableValue = totalLoss * crf * MAX_RECOVERY_RATE;

    // Step 8b: Split recoverable value by intervention type
    const systemRecoverable = recoverableValue * SYSTEM_LOSS_WEIGHT;
    const leadershipRecoverable = recoverableValue * LEADERSHIP_LOSS_WEIGHT;
    const combinedRecovery = systemRecoverable + leadershipRecoverable;

    // Step 9: Recovery Points and Final Capture Rate
    const recoveryPoints = recoverableValue / valueAtStake;
    const finalCaptureRate = AVERAGE_CAPTURE_RATE + recoveryPoints;

    // Step 10: Ceiling check
    const isValid = finalCaptureRate <= SUCCESSFUL_CEILING;

    // Determine zones
    const getZone = (score) => {
      if (score < 0.6) return 'foundational';
      if (score < 0.75) return 'stretch';
      return 'mastery';
    };

    return {
      // Inputs
      valueAtStake,
      totalLoss,
      systemLossPool,
      leadershipLossPool,

      // DCF calculations (raw = what we use)
      dcf,
      rawDcf,
      personaDcf: baseline.dcf,
      dcfZone: getZone(dcf),

      // LPFF calculations (raw = what we use)
      lpff,
      rawLpff,
      personaLpff: baseline.lpff,
      lpffZone: getZone(lpff),

      // CRF breakdown
      systemComponent,
      leadershipComponent,
      crf,

      // Recovery
      recoverableValue,
      systemRecoverable,
      leadershipRecoverable,
      combinedRecovery,
      recoveryPoints,
      finalCaptureRate,

      // Validation
      isValid,
      ceiling: SUCCESSFUL_CEILING
    };
  }, [valueAtStake, dependencyScores, anxietyScores, personaId]);

  const results = calculateResults();

  // Calculate simplified algorithm results for comparison
  const simplifiedResults = calculateSimplifiedResults(
    valueAtStake,
    results.dcf,
    results.lpff
  );

  // Get score classification
  const getScoreClass = (score) => {
    if (score <= 2) return 'critical';
    if (score <= 4) return 'weak';
    if (score <= 6) return 'moderate';
    if (score <= 8) return 'strong';
    return 'excellent';
  };

  // Get top 3 weakest items with full details from framework data
  const getTop3Dependencies = () => {
    const items = Object.entries(dependencyScores).map(([key, score], index) => {
      const depData = frameworkData.dependencies[index] || {};
      return {
        id: index + 1,
        label: DEPENDENCY_LABELS[index],
        fullLabel: depData.dependencyLabel || DEPENDENCY_LABELS[index],
        summary: depData.top3Summary || DEPENDENCY_DESCRIPTIONS[index],
        impact: depData.impactStatement || '',
        actionSummary: depData.actionSummary || '',
        actionStatement: depData.actionStatement || '',
        score,
        key
      };
    });
    // Sort ascending (lowest scores first) - these are the weakest areas requiring attention
    return items.sort((a, b) => a.score - b.score).slice(0, 3);
  };

  const getTop3Anxieties = () => {
    const items = Object.entries(anxietyScores).map(([key, score], index) => {
      const anxData = frameworkData.anxieties[index] || {};
      return {
        id: index + 1,
        label: ANXIETY_LABELS[index],
        fullLabel: anxData.genericAnxiety || ANXIETY_LABELS[index],
        summary: anxData.top3Summary || anxData.psychologicalPattern || ANXIETY_DESCRIPTIONS[index],
        impact: anxData.impactStatement || '',
        actionSummary: anxData.actionSummary || '',
        actionStatement: anxData.actionStatement || '',
        score,
        key
      };
    });
    // Sort ascending (lowest scores first) - these are the weakest areas requiring attention
    return items.sort((a, b) => a.score - b.score).slice(0, 3);
  };

  const top3Dependencies = getTop3Dependencies();
  const top3Anxieties = getTop3Anxieties();

  // Handle slider change
  const handleDependencyChange = (index, value) => {
    setDependencyScores(prev => ({
      ...prev,
      [`dep${index}`]: parseInt(value)
    }));
  };

  const handleAnxietyChange = (index, value) => {
    setAnxietyScores(prev => ({
      ...prev,
      [`anxiety${index}`]: parseInt(value)
    }));
  };

  // Load data from selected diagnostics
  const handleLoadDiagnosticData = () => {
    // Load System Burden data if selected
    if (selectedSystemDiagnosticId) {
      const systemDiag = systemBurdenDiagnostics.find(d => d.id === parseInt(selectedSystemDiagnosticId));
      if (systemDiag) {
        const newDepScores = {};
        for (let i = 1; i <= 14; i++) {
          const score = systemDiag.scores?.[`dep${i}`];
          newDepScores[`dep${i}`] = score !== null && score !== undefined ? score : 5;
        }
        setDependencyScores(newDepScores);
        if (systemDiag.valueAtStake) {
          setValueAtStake(systemDiag.valueAtStake);
        }
      }
    }

    // Load Leadership Burden data if selected
    if (selectedLeadershipDiagnosticId) {
      const leadershipDiag = leadershipBurdenDiagnostics.find(d => d.id === parseInt(selectedLeadershipDiagnosticId));
      if (leadershipDiag) {
        const newAnxScores = {};
        for (let i = 1; i <= 10; i++) {
          const score = leadershipDiag.scores?.[`anx${i}`];
          newAnxScores[`anxiety${i}`] = score !== null && score !== undefined ? score : 5;
        }
        setAnxietyScores(newAnxScores);
        // Only update value at stake from leadership if no system burden selected
        if (leadershipDiag.valueAtStake && !selectedSystemDiagnosticId) {
          setValueAtStake(leadershipDiag.valueAtStake);
        }
      }
    }
  };

  // Load data from selected scenario
  const handleLoadScenario = () => {
    if (!selectedScenarioId) return;

    const scenario = scenarios.find(s => s.id === parseInt(selectedScenarioId));
    if (!scenario) return;

    // Load persona
    if (scenario.personaId) {
      setPersonaId(scenario.personaId);
    }

    // Load value at stake
    if (scenario.valueAtStake !== null && scenario.valueAtStake !== undefined) {
      setValueAtStake(scenario.valueAtStake);
    }

    // Load dependency scores
    if (scenario.scores?.dependencies) {
      const newDepScores = {};
      for (let i = 1; i <= 14; i++) {
        const score = scenario.scores.dependencies[`dep${i}`];
        newDepScores[`dep${i}`] = score !== null && score !== undefined ? score : 5;
      }
      setDependencyScores(newDepScores);
    }

    // Load anxiety scores
    if (scenario.scores?.anxieties) {
      const newAnxScores = {};
      for (let i = 1; i <= 10; i++) {
        const score = scenario.scores.anxieties[`anx${i}`];
        newAnxScores[`anxiety${i}`] = score !== null && score !== undefined ? score : 5;
      }
      setAnxietyScores(newAnxScores);
    }
  };

  // Delete selected scenario
  const handleDeleteScenario = async () => {
    if (!selectedScenarioId) return;

    const scenario = scenarios.find(s => s.id === parseInt(selectedScenarioId));
    if (!scenario) return;

    // Prevent deletion of default scenarios
    if (scenario.isDefault) {
      alert('Cannot delete default scenarios.');
      return;
    }

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${scenario.scenarioName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/scenarios?id=${selectedScenarioId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        // Refresh scenarios list
        const refreshUrl = selectedLeaderId
          ? `/api/admin/scenarios?leaderId=${selectedLeaderId}`
          : '/api/admin/scenarios';
        const refreshResponse = await fetch(refreshUrl, { credentials: 'include' });
        const refreshData = await refreshResponse.json();
        if (refreshData.success) {
          setScenarios(refreshData.scenarios || []);
        }
        // Clear selection
        setSelectedScenarioId('');
      } else {
        alert(data.error || 'Failed to delete scenario');
      }
    } catch (err) {
      console.error('Delete scenario error:', err);
      alert('Failed to delete scenario');
    }
  };

  // Reset sliders to neutral position (DCF=0.70 for DCF Normalized=0)
  const handleReset = () => {
    // Reset dependency scores to 7 (produces DCF = 0.70, DCF Normalized = 0)
    setDependencyScores(
      Array(14).fill(7).reduce((acc, val, i) => ({ ...acc, [`dep${i + 1}`]: val }), {})
    );
    // Reset anxiety scores to 5 (neutral)
    setAnxietyScores(
      Array(10).fill(5).reduce((acc, val, i) => ({ ...acc, [`anxiety${i + 1}`]: val }), {})
    );
    // Reset persona to Mark
    setPersonaId('mark');
    // Reset value at stake to default
    setValueAtStake(15);
    // Clear selections
    setSelectedLeaderId('');
    setSelectedSystemDiagnosticId('');
    setSelectedLeadershipDiagnosticId('');
    setSelectedScenarioId('');
  };

  // Save current state as a new scenario
  const handleSaveScenario = async () => {
    if (!newScenarioName.trim()) return;

    setSaving(true);
    try {
      // Build the payload
      const payload = {
        scenarioName: newScenarioName.trim(),
        description: newScenarioDescription.trim() || null,
        leaderId: saveForLeader && selectedLeaderId ? parseInt(selectedLeaderId) : null,
        personaId,
        valueAtStake,
        scores: {
          dependencies: dependencyScores,
          anxieties: Object.entries(anxietyScores).reduce((acc, [key, val]) => {
            // Convert anxiety1 -> anx1 format
            const anxKey = key.replace('anxiety', 'anx');
            acc[anxKey] = val;
            return acc;
          }, {})
        }
      };

      const response = await fetch('/api/admin/scenarios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        // Refresh scenarios list
        const refreshUrl = selectedLeaderId
          ? `/api/admin/scenarios?leaderId=${selectedLeaderId}`
          : '/api/admin/scenarios';
        const refreshResponse = await fetch(refreshUrl, { credentials: 'include' });
        const refreshData = await refreshResponse.json();
        if (refreshData.success) {
          setScenarios(refreshData.scenarios || []);
          // Select the newly created scenario
          if (data.scenarioId) {
            setSelectedScenarioId(data.scenarioId.toString());
          }
        }

        // Reset modal state and close
        setShowSaveModal(false);
        setNewScenarioName('');
        setNewScenarioDescription('');
        setSaveForLeader(false);
      } else {
        alert(data.error || 'Failed to save scenario');
      }
    } catch (err) {
      console.error('Save scenario error:', err);
      alert('Failed to save scenario');
    } finally {
      setSaving(false);
    }
  };

  // Helper to format diagnostic dropdown text
  const formatDiagnosticOption = (diag, type) => {
    const date = new Date(diag.completedAt).toLocaleDateString();
    const value = diag.valueAtStake ? `£${diag.valueAtStake}M` : '';

    // Calculate zone from stored DCF/LPFF or scores
    let zone = '';
    let percentage = '';

    if (type === 'system') {
      // Calculate DCF from dependency scores using weighted formula
      const scores = diag.scores || {};
      let totalWeightedContribution = 0;
      let totalWeight = 0;
      DEPENDENCY_WEIGHTS.forEach(dep => {
        const score = scores[`dep${dep.id}`] ?? scores[`score${dep.id}`] ?? 0;
        totalWeightedContribution += dep.weight * score;
        totalWeight += dep.weight;
      });
      if (totalWeight > 0) {
        const dcf = totalWeightedContribution / (totalWeight * 10);
        percentage = `${(dcf * 100).toFixed(0)}%`;
        zone = dcf < 0.6 ? 'Foundational' : dcf < 0.75 ? 'Stretch' : 'Mastery';
      }
    } else {
      // Calculate LPFF from anxiety scores
      const scores = diag.scores || {};
      const anxValues = Object.values(scores).filter(v => v !== null && v !== undefined);
      if (anxValues.length > 0) {
        const invertedSum = anxValues.reduce((sum, score) => sum + (10 - score), 0);
        const lpff = invertedSum / 100;
        percentage = `${(lpff * 100).toFixed(0)}%`;
        zone = lpff < 0.6 ? 'Foundational' : lpff < 0.75 ? 'Stretch' : 'Mastery';
      }
    }

    const parts = [date];
    if (value) parts.push(value);
    if (zone) parts.push(`${zone} (${percentage})`);

    return parts.join(' • ');
  };

  // Helper to format scenario dropdown text
  const formatScenarioOption = (scenario) => {
    const name = scenario.scenarioName;
    const desc = scenario.description;

    if (desc) {
      // Truncate description if too long
      const truncatedDesc = desc.length > 40 ? desc.substring(0, 40) + '...' : desc;
      return `${name} - ${truncatedDesc}`;
    }
    return name;
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value >= 1) {
      return `£${value.toFixed(2)}M`;
    }
    return `£${(value * 1000).toFixed(0)}K`;
  };

  // Format percentage
  const formatPercent = (value) => `${(value * 100).toFixed(1)}%`;

  if (loading) {
    return (
      <AdminLayout title="Dynamic Results">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-lg shadow-sm p-5">
          {/* Top Row: Title and Toggle */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold text-daring-navy">Combined Dynamic Results</h1>
              <p className="text-sm text-daring-grey mt-1 flex items-center gap-2 flex-wrap">
                System Burden + Leadership Burden Analysis
                {/* Calculations Toggle Switch */}
                <label className="inline-flex items-center cursor-pointer select-none ml-2" title="Toggle calculations panel">
                  <span className="text-xs text-daring-grey mr-1">Calculations</span>
                  <input
                    type="checkbox"
                    checked={showCalculations}
                    onChange={() => setShowCalculations(!showCalculations)}
                    className="sr-only peer"
                  />
                  <div className="relative w-8 h-4 bg-gray-300 rounded-full peer-checked:bg-amber-500 transition-colors">
                    <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all duration-200 ${showCalculations ? 'left-[18px]' : 'left-0.5'}`}></div>
                  </div>
                </label>
                {/* Top 3 Toggle Switch */}
                <label className="inline-flex items-center cursor-pointer select-none ml-2" title="Toggle Top 3 panels">
                  <span className="text-xs text-daring-grey mr-1">Alerts</span>
                  <input
                    type="checkbox"
                    checked={showTop3}
                    onChange={() => setShowTop3(!showTop3)}
                    className="sr-only peer"
                  />
                  <div className="relative w-8 h-4 bg-gray-300 rounded-full peer-checked:bg-amber-500 transition-colors">
                    <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all duration-200 ${showTop3 ? 'left-[18px]' : 'left-0.5'}`}></div>
                  </div>
                </label>
                {/* Next Steps Toggle Switch */}
                <label className="inline-flex items-center cursor-pointer select-none ml-2" title="Toggle Next Steps panels">
                  <span className="text-xs text-daring-grey mr-1">Actions</span>
                  <input
                    type="checkbox"
                    checked={showNextSteps}
                    onChange={() => setShowNextSteps(!showNextSteps)}
                    className="sr-only peer"
                  />
                  <div className="relative w-8 h-4 bg-gray-300 rounded-full peer-checked:bg-amber-500 transition-colors">
                    <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-all duration-200 ${showNextSteps ? 'left-[18px]' : 'left-0.5'}`}></div>
                  </div>
                </label>
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleLoadScenario}
                disabled={!selectedScenarioId}
                className={`px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap ${
                  selectedScenarioId
                    ? 'bg-daring-navy text-white hover:bg-daring-navy/90'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Load Scenario
              </button>
              <button
                onClick={handleLoadDiagnosticData}
                disabled={!selectedSystemDiagnosticId && !selectedLeadershipDiagnosticId}
                className={`px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap ${
                  selectedSystemDiagnosticId || selectedLeadershipDiagnosticId
                    ? 'bg-daring-navy text-white hover:bg-daring-navy/90'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Load Diagnostic
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 rounded-md text-sm font-semibold border bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Middle Row: Dropdowns - Equal width grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 pb-4 border-b border-gray-200">
            {/* Leader Select */}
            <div className="flex flex-col gap-1">
              <label className="text-xs uppercase tracking-wide text-daring-grey font-semibold">
                Select Leader
              </label>
              <select
                value={selectedLeaderId}
                onChange={(e) => setSelectedLeaderId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
              >
                <option value="">-- Select Leader --</option>
                {leaders.map(leader => (
                  <option key={leader.id} value={leader.id}>
                    {leader.firstName} {leader.lastName} • {leader.email}
                  </option>
                ))}
              </select>
            </div>

            {/* System Burden Diagnostic Select */}
            <div className="flex flex-col gap-1">
              <label className="text-xs uppercase tracking-wide text-daring-grey font-semibold">
                System Burden Diagnostic
              </label>
              <select
                value={selectedSystemDiagnosticId}
                onChange={(e) => setSelectedSystemDiagnosticId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
                disabled={!selectedLeaderId}
              >
                <option value="">-- Default Values --</option>
                {systemBurdenDiagnostics.map(diag => (
                  <option key={diag.id} value={diag.id}>
                    {formatDiagnosticOption(diag, 'system')}
                  </option>
                ))}
              </select>
            </div>

            {/* Leadership Burden Diagnostic Select */}
            <div className="flex flex-col gap-1">
              <label className="text-xs uppercase tracking-wide text-daring-grey font-semibold">
                Leadership Burden Diagnostic
              </label>
              <select
                value={selectedLeadershipDiagnosticId}
                onChange={(e) => setSelectedLeadershipDiagnosticId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-full"
                disabled={!selectedLeaderId}
              >
                <option value="">-- Default Values --</option>
                {leadershipBurdenDiagnostics.map(diag => (
                  <option key={diag.id} value={diag.id}>
                    {formatDiagnosticOption(diag, 'leadership')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Scenarios Row */}
          <div className="flex items-end gap-4 flex-wrap mb-4 pb-4 border-b border-gray-200">
            <div className="flex flex-col gap-1 flex-1 min-w-[280px]">
              <label className="text-xs uppercase tracking-wide text-daring-grey font-semibold">
                Scenarios
              </label>
              <div className="flex gap-2">
                <select
                  value={selectedScenarioId}
                  onChange={(e) => setSelectedScenarioId(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1"
                >
                  <option value="">-- Select Scenario --</option>
                  {/* Leader-specific scenarios */}
                  {scenarios.filter(s => s.leaderId).length > 0 && (
                    <optgroup label={`${leaders.find(l => l.id === parseInt(selectedLeaderId))?.firstName || 'Leader'}'s Scenarios`}>
                      {scenarios.filter(s => s.leaderId).map(scenario => (
                        <option key={scenario.id} value={scenario.id}>
                          {formatScenarioOption(scenario)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {/* Template/Generic scenarios */}
                  {scenarios.filter(s => !s.leaderId).length > 0 && (
                    <optgroup label="Template Scenarios">
                      {scenarios.filter(s => !s.leaderId).map(scenario => (
                        <option key={scenario.id} value={scenario.id}>
                          {formatScenarioOption(scenario)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <button
                  onClick={() => {
                    // Pre-check "save for leader" if a leader is selected
                    if (selectedLeaderId) {
                      setSaveForLeader(true);
                    }
                    setShowSaveModal(true);
                  }}
                  className="px-3 py-2 rounded-md text-sm font-semibold whitespace-nowrap bg-green-600 text-white hover:bg-green-700"
                >
                  Save Scenario
                </button>
                <button
                  onClick={handleDeleteScenario}
                  disabled={!selectedScenarioId || scenarios.find(s => s.id === parseInt(selectedScenarioId))?.isDefault}
                  className={`px-3 py-2 rounded-md text-sm font-semibold whitespace-nowrap ${
                    selectedScenarioId && !scenarios.find(s => s.id === parseInt(selectedScenarioId))?.isDefault
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                  title={scenarios.find(s => s.id === parseInt(selectedScenarioId))?.isDefault ? 'Cannot delete default scenarios' : 'Delete selected scenario'}
                >
                  Delete
                </button>
              </div>
              {selectedScenarioId && scenarios.find(s => s.id === parseInt(selectedScenarioId))?.description && (
                <p className="text-xs text-daring-grey mt-1 italic">
                  {scenarios.find(s => s.id === parseInt(selectedScenarioId))?.description}
                </p>
              )}
            </div>
          </div>

          {/* Bottom Row: Value at Stake and Persona */}
          <div className="flex items-center gap-6 flex-wrap">
            {/* Value at Stake */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-daring-navy whitespace-nowrap">
                Value at Stake:
              </label>
              <div className="flex items-center gap-1">
                <span className="text-sm text-daring-grey">£</span>
                <input
                  type="number"
                  min="0.1"
                  max="1000"
                  step="0.1"
                  value={valueAtStake}
                  onChange={(e) => setValueAtStake(parseFloat(e.target.value) || 0.1)}
                  className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm text-right font-mono"
                />
                <span className="text-sm text-daring-grey">M</span>
              </div>
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-gray-300 hidden sm:block" />

            {/* Persona Baseline */}
            <div className="flex items-center gap-4">
              <span className="text-sm font-semibold text-daring-navy whitespace-nowrap">Persona:</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="persona"
                  value="mark"
                  checked={personaId === 'mark'}
                  onChange={(e) => setPersonaId(e.target.value)}
                  className="w-4 h-4 accent-daring-navy border-2 border-gray-400"
                />
                <span className="text-sm">Mark <span className="text-daring-grey text-xs">(DCF: 0.70, LPFF: 0.55)</span></span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="persona"
                  value="sarah"
                  checked={personaId === 'sarah'}
                  onChange={(e) => setPersonaId(e.target.value)}
                  className="w-4 h-4 accent-daring-navy border-2 border-gray-400"
                />
                <span className="text-sm">Sarah <span className="text-daring-grey text-xs">(DCF: 0.65, LPFF: 0.65)</span></span>
              </label>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
            {error}
          </div>
        )}

        {/* Algorithm v7.1 Calculation Panel */}
        {showCalculations && (
          <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-5">
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm font-bold text-emerald-800 uppercase tracking-wide">
                Algorithm v7.1 Calculation Panel
              </span>
              <span className="text-xs text-emerald-700">Pie segments: Foundation + Extra + Stretch + Lost = 100%</span>
            </div>

            {/* Constants Bar */}
            <div className="bg-emerald-100 rounded-md p-3 mb-4 flex flex-wrap items-center gap-6">
              <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                Illustration Constraints:
              </span>
              <span className="text-xs text-emerald-900">
                <span className="text-emerald-700">Permanently Lost</span>{' '}
                <span className="font-bold font-mono">33%</span>
              </span>
              <span className="text-emerald-400">|</span>
              <span className="text-xs text-emerald-900">
                <span className="text-emerald-700">Stretch Potential</span>{' '}
                <span className="font-bold font-mono">10%</span>
              </span>
              <span className="text-emerald-400">|</span>
              <span className="text-xs text-emerald-900">
                <span className="text-emerald-700">Capture Range</span>{' '}
                <span className="font-bold font-mono">57% - 67%</span>
              </span>
              <span className="text-emerald-400">|</span>
              <span className="text-xs text-emerald-900">
                <span className="text-emerald-700">DCF Zone</span>{' '}
                <span className="font-bold font-mono">0.70 - 0.85</span>
              </span>
              <span className="text-emerald-400">|</span>
              <span className="text-xs text-emerald-900">
                <span className="text-emerald-700">LPFF Zone</span>{' '}
                <span className="font-bold font-mono">0.50 - 0.85</span>
              </span>
            </div>

            {/* Calculations Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Inputs & Normalization */}
              <div className="bg-white rounded-md p-4 border border-emerald-200">
                <div className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-3 pb-2 border-b border-emerald-200">
                  Normalization
                </div>
                <div className="space-y-2 text-xs">
                  <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                    <span className="text-emerald-900">DCF</span>
                    <span className="font-semibold font-mono text-daring-navy text-right">{results.dcf.toFixed(3)}</span>
                    <span className="text-emerald-600 font-mono text-[10px] text-right">input</span>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                    <span className="text-emerald-900">DCF Normalized</span>
                    <span className="font-semibold font-mono text-daring-navy text-right">{simplifiedResults.dcfNormalized.toFixed(3)}</span>
                    <span className="text-emerald-600 font-mono text-[10px] text-right">(dcf-.70)/.15</span>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                    <span className="text-emerald-900">LPFF</span>
                    <span className="font-semibold font-mono text-daring-navy text-right">{results.lpff.toFixed(3)}</span>
                    <span className="text-emerald-600 font-mono text-[10px] text-right">input</span>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                    <span className="text-emerald-900">LPFF Normalized</span>
                    <span className="font-semibold font-mono text-daring-navy text-right">{simplifiedResults.lpffNormalized.toFixed(3)}</span>
                    <span className="text-emerald-600 font-mono text-[10px] text-right">(lpff-.50)/.35</span>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center bg-emerald-50 -mx-2 px-2 py-1 rounded">
                    <span className="text-emerald-900 font-semibold">Combined Factor</span>
                    <span className="font-semibold font-mono text-emerald-700 text-right">{simplifiedResults.combinedFactor.toFixed(3)}</span>
                    <span className="text-emerald-600 font-mono text-[10px] text-right">avg</span>
                  </div>
                </div>
              </div>

              {/* Capture Calculation */}
              <div className="bg-white rounded-md p-4 border border-emerald-200">
                <div className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-3 pb-2 border-b border-emerald-200">
                  Capture Rate
                </div>
                <div className="space-y-2 text-xs">
                  <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                    <span className="text-emerald-900">Baseline (57%)</span>
                    <span className="font-semibold font-mono text-daring-navy text-right">0.570</span>
                    <span className="text-emerald-600 font-mono text-[10px] text-right">fixed</span>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                    <span className="text-emerald-900">Stretch Add</span>
                    <span className="font-semibold font-mono text-daring-navy text-right">{(simplifiedResults.combinedFactor * 0.10).toFixed(3)}</span>
                    <span className="text-emerald-600 font-mono text-[10px] text-right">cf × 0.10</span>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center bg-emerald-50 -mx-2 px-2 py-1 rounded">
                    <span className="text-emerald-900 font-semibold">Capture Rate</span>
                    <span className="font-semibold font-mono text-emerald-700 text-right">{(simplifiedResults.captureRate * 100).toFixed(1)}%</span>
                    <span className="text-emerald-600 font-mono text-[10px] text-right">.57 + add</span>
                  </div>
                  <div className="mt-3 pt-2 border-t border-emerald-100">
                    <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                      <span className="text-emerald-900">Zone</span>
                      {(() => {
                        // Zone thresholds aligned with calculateSimplifiedResults in algorithms.js
                        const isFoundational = results.dcf < 0.70 || results.lpff < 0.50;
                        const isMastery = results.dcf >= 0.85 && results.lpff >= 0.85;
                        const zoneName = isFoundational ? 'Foundational' : isMastery ? 'Mastery' : 'Stretch';
                        const zoneColor = isFoundational ? 'text-red-600' : isMastery ? 'text-green-600' : 'text-yellow-600';
                        return (
                          <span className={`font-semibold text-right ${zoneColor}`}>
                            {zoneName}
                          </span>
                        );
                      })()}
                      <span className="text-emerald-600 font-mono text-[10px] text-right"></span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pie Segments */}
              <div className="bg-white rounded-md p-4 border border-emerald-200">
                <div className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-3 pb-2 border-b border-emerald-200">
                  Pie Segments
                </div>
                {(() => {
                  const FOUNDATIONAL_PCT = 57;
                  const STRETCH_POOL_PCT = 10;
                  const PERMANENTLY_LOST_PCT = 33;

                  // Zone thresholds aligned with calculateSimplifiedResults in algorithms.js
                  const DCF_FOUNDATIONAL = 0.70;
                  const DCF_MASTERY = 0.85;
                  const LPFF_FOUNDATIONAL = 0.50;
                  const LPFF_MASTERY = 0.85;

                  const dcfNorm = Math.max(0, Math.min(1, (results.dcf - DCF_FOUNDATIONAL) / (DCF_MASTERY - DCF_FOUNDATIONAL)));
                  const lpffNorm = Math.max(0, Math.min(1, (results.lpff - LPFF_FOUNDATIONAL) / (LPFF_MASTERY - LPFF_FOUNDATIONAL)));
                  const combinedFactor = (dcfNorm * 0.685) + (lpffNorm * 0.315);

                  const isFoundational = results.dcf < DCF_FOUNDATIONAL || results.lpff < LPFF_FOUNDATIONAL;
                  const isMastery = results.dcf >= DCF_MASTERY && results.lpff >= LPFF_MASTERY;

                  let extraValuePct, stretchPct;
                  if (isFoundational) {
                    extraValuePct = 0;
                    stretchPct = STRETCH_POOL_PCT;
                  } else if (isMastery) {
                    extraValuePct = STRETCH_POOL_PCT;
                    stretchPct = 0;
                  } else {
                    extraValuePct = STRETCH_POOL_PCT * combinedFactor;
                    stretchPct = STRETCH_POOL_PCT - extraValuePct;
                  }

                  const foundationalAmt = (FOUNDATIONAL_PCT / 100) * valueAtStake;
                  const extraValueAmt = (extraValuePct / 100) * valueAtStake;
                  const stretchAmt = (stretchPct / 100) * valueAtStake;
                  const permanentlyLostAmt = (PERMANENTLY_LOST_PCT / 100) * valueAtStake;

                  return (
                    <div className="space-y-2 text-xs">
                      <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                        <span className="text-emerald-900 flex items-center gap-1">
                          <span className="w-2 h-2 rounded-sm bg-green-600"></span>
                          Foundational Value
                        </span>
                        <span className="font-semibold font-mono text-green-600 text-right">{formatCurrency(foundationalAmt)}</span>
                        <span className="text-emerald-600 font-mono text-[10px] text-right">{FOUNDATIONAL_PCT}%</span>
                      </div>
                      <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                        <span className="text-emerald-900 flex items-center gap-1">
                          <span className="w-2 h-2 rounded-sm bg-green-500"></span>
                          Extra Value
                        </span>
                        <span className="font-semibold font-mono text-green-500 text-right">{formatCurrency(extraValueAmt)}</span>
                        <span className="text-emerald-600 font-mono text-[10px] text-right">{extraValuePct.toFixed(1)}%</span>
                      </div>
                      <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                        <span className="text-emerald-900 flex items-center gap-1">
                          <span className="w-2 h-2 rounded-sm bg-sky-300"></span>
                          Stretch Potential
                        </span>
                        <span className="font-semibold font-mono text-sky-600 text-right">{formatCurrency(stretchAmt)}</span>
                        <span className="text-emerald-600 font-mono text-[10px] text-right">{stretchPct.toFixed(1)}%</span>
                      </div>
                      <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                        <span className="text-emerald-900 flex items-center gap-1">
                          <span className="w-2 h-2 rounded-sm bg-red-600"></span>
                          Permanently Lost
                        </span>
                        <span className="font-semibold font-mono text-red-600 text-right">{formatCurrency(permanentlyLostAmt)}</span>
                        <span className="text-emerald-600 font-mono text-[10px] text-right">{PERMANENTLY_LOST_PCT}%</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-emerald-100">
                        <div className="grid grid-cols-[1fr_auto_80px] gap-2 items-center">
                          <span className="text-emerald-900 font-semibold">Total</span>
                          <span className="font-semibold font-mono text-daring-navy text-right">{formatCurrency(valueAtStake)}</span>
                          <span className="text-emerald-600 font-mono text-[10px] text-right">100%</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Output Row */}
            {(() => {
              const FOUNDATIONAL_PCT = 57;
              const STRETCH_POOL_PCT = 10;
              const PERMANENTLY_LOST_PCT = 33;

              // Zone thresholds aligned with calculateSimplifiedResults in algorithms.js
              const DCF_FOUNDATIONAL = 0.70;
              const DCF_MASTERY = 0.85;
              const LPFF_FOUNDATIONAL = 0.50;
              const LPFF_MASTERY = 0.85;

              const dcfNorm = Math.max(0, Math.min(1, (results.dcf - DCF_FOUNDATIONAL) / (DCF_MASTERY - DCF_FOUNDATIONAL)));
              const lpffNorm = Math.max(0, Math.min(1, (results.lpff - LPFF_FOUNDATIONAL) / (LPFF_MASTERY - LPFF_FOUNDATIONAL)));
              const combinedFactor = (dcfNorm * 0.685) + (lpffNorm * 0.315);

              const isFoundational = results.dcf < DCF_FOUNDATIONAL || results.lpff < LPFF_FOUNDATIONAL;
              const isMastery = results.dcf >= DCF_MASTERY && results.lpff >= LPFF_MASTERY;

              let extraValuePct, stretchPct;
              if (isFoundational) {
                extraValuePct = 0;
                stretchPct = STRETCH_POOL_PCT;
              } else if (isMastery) {
                extraValuePct = STRETCH_POOL_PCT;
                stretchPct = 0;
              } else {
                extraValuePct = STRETCH_POOL_PCT * combinedFactor;
                stretchPct = STRETCH_POOL_PCT - extraValuePct;
              }

              const capturedPct = FOUNDATIONAL_PCT + extraValuePct;
              const capturedAmt = (capturedPct / 100) * valueAtStake;
              const stretchAmt = (stretchPct / 100) * valueAtStake;
              const permanentlyLostAmt = (PERMANENTLY_LOST_PCT / 100) * valueAtStake;

              return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-green-50 rounded-md p-3 border border-green-400">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-green-800">Captured Value</span>
                      <span className="font-bold font-mono text-green-700 text-lg">{formatCurrency(capturedAmt)}</span>
                      <span className="text-xs text-green-600 font-mono">({capturedPct.toFixed(1)}%)</span>
                    </div>
                  </div>
                  <div className="bg-sky-50 rounded-md p-3 border border-sky-400">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-sky-800">Recoverable (Stretch)</span>
                      <span className="font-bold font-mono text-sky-700 text-lg">{formatCurrency(stretchAmt)}</span>
                      <span className="text-xs text-sky-600 font-mono">({stretchPct.toFixed(1)}%)</span>
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-md p-3 border border-red-400">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-red-800">Permanently Lost</span>
                      <span className="font-bold font-mono text-red-700 text-lg">{formatCurrency(permanentlyLostAmt)}</span>
                      <span className="text-xs text-red-600 font-mono">({PERMANENTLY_LOST_PCT}%)</span>
                    </div>
                  </div>
                </div>
              );
            })()}

          </div>
        )}

        {/* Summary Bar */}
        {(() => {
          // Calculate pie chart values for summary bar (same logic as VALUE ANALYSIS pie)
          // Zone thresholds aligned with calculateSimplifiedResults in algorithms.js
          const DCF_FOUNDATIONAL = 0.70;
          const DCF_MASTERY = 0.85;
          const LPFF_FOUNDATIONAL = 0.50;
          const LPFF_MASTERY = 0.85;
          const FOUNDATIONAL_PCT = 57;
          const STRETCH_POOL_PCT = 10;

          const isFoundational = results.dcf < DCF_FOUNDATIONAL || results.lpff < LPFF_FOUNDATIONAL;
          const isMastery = results.dcf >= DCF_MASTERY && results.lpff >= LPFF_MASTERY;

          let extraValuePct, stretchPct;
          if (isFoundational) {
            extraValuePct = 0;
            stretchPct = STRETCH_POOL_PCT;
          } else if (isMastery) {
            extraValuePct = STRETCH_POOL_PCT;
            stretchPct = 0;
          } else {
            const dcfNorm = Math.max(0, Math.min(1, (results.dcf - DCF_FOUNDATIONAL) / (DCF_MASTERY - DCF_FOUNDATIONAL)));
            const lpffNorm = Math.max(0, Math.min(1, (results.lpff - LPFF_FOUNDATIONAL) / (LPFF_MASTERY - LPFF_FOUNDATIONAL)));
            const combinedFactor = (dcfNorm * 0.685) + (lpffNorm * 0.315);
            extraValuePct = STRETCH_POOL_PCT * combinedFactor;
            stretchPct = STRETCH_POOL_PCT - extraValuePct;
          }

          const foundationalAmt = (FOUNDATIONAL_PCT / 100) * valueAtStake;
          const extraValueAmt = (extraValuePct / 100) * valueAtStake;
          const stretchAmt = (stretchPct / 100) * valueAtStake;

          // Zone-based styling for the summary bar
          // Using very light shades for backgrounds with dark text for readability
          const zoneStyles = isFoundational
            ? { bg: 'bg-red-50 border border-red-200', text: 'text-red-900', label: 'text-red-700', divider: 'bg-red-200', accent: 'text-red-600' }
            : isMastery
            ? { bg: 'bg-green-50 border border-green-200', text: 'text-green-900', label: 'text-green-700', divider: 'bg-green-200', accent: 'text-green-600' }
            : { bg: 'bg-yellow-50 border border-yellow-200', text: 'text-yellow-900', label: 'text-yellow-700', divider: 'bg-yellow-200', accent: 'text-yellow-600' };

          return (
            <div className={`${zoneStyles.bg} rounded-lg p-4 flex flex-wrap justify-between items-center gap-4`}>
              <div className="text-center">
                <div className={`text-xs uppercase tracking-wide ${zoneStyles.label}`}>Value at Stake</div>
                <div className={`text-2xl font-bold ${zoneStyles.text}`}>{formatCurrency(valueAtStake)}</div>
              </div>
              <div className={`w-px h-10 ${zoneStyles.divider} hidden md:block`} />
              <div className="text-center">
                <div className={`text-xs uppercase tracking-wide ${zoneStyles.label}`}>Permanently Lost</div>
                <div className="text-2xl font-bold text-red-600">{formatCurrency(simplifiedResults.permanentlyLost)}</div>
              </div>
              <div className={`w-px h-10 ${zoneStyles.divider} hidden md:block`} />
              <div className="text-center">
                <div className={`text-xs uppercase tracking-wide ${zoneStyles.label}`}>Foundational Value</div>
                <div className="text-2xl font-bold text-green-600">{formatCurrency(foundationalAmt)}</div>
              </div>
              <div className={`w-px h-10 ${zoneStyles.divider} hidden md:block`} />
              <div className="text-center">
                <div className={`text-xs uppercase tracking-wide ${zoneStyles.label}`}>Extra Value</div>
                <div className="text-2xl font-bold text-green-500">{formatCurrency(extraValueAmt)}</div>
              </div>
              <div className={`w-px h-10 ${zoneStyles.divider} hidden md:block`} />
              <div className="text-center">
                <div className={`text-xs uppercase tracking-wide ${zoneStyles.label}`}>Stretch Potential</div>
                <div className="text-2xl font-bold text-sky-600">{formatCurrency(stretchAmt)}</div>
              </div>
              <div className={`w-px h-10 ${zoneStyles.divider} hidden md:block`} />
              <div className="text-center">
                <div className={`text-xs uppercase tracking-wide ${zoneStyles.label}`}>Capture Rate</div>
                <div className={`text-2xl font-bold ${zoneStyles.text}`}>{formatPercent((FOUNDATIONAL_PCT + extraValuePct) / 100)}</div>
              </div>
            </div>
          );
        })()}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT COLUMN: System Burden */}
          <div className="flex flex-col gap-5">
            {/* Value Analysis Pie Chart */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h3 className="text-sm font-bold text-daring-navy uppercase tracking-wide mb-4">
                Value Analysis
              </h3>
              {(() => {
                // Zone-aware pie chart segments using combined factor (DCF + LPFF)
                // Fixed segments: Foundational (57%), Permanently Lost (33%)
                // Dynamic segments: Extra value gaining (0-10%), Your stretch (10-0%)

                // Zone thresholds aligned with calculateSimplifiedResults in algorithms.js
                const DCF_FOUNDATIONAL = 0.70;
                const DCF_MASTERY = 0.85;
                const LPFF_FOUNDATIONAL = 0.50;
                const LPFF_MASTERY = 0.85;

                const FOUNDATIONAL_PCT = 57;
                const STRETCH_POOL_PCT = 10;
                const PERMANENTLY_LOST_PCT = 33;

                // Determine overall zone based on both DCF and LPFF
                // Foundational: DCF < 70% OR LPFF < 50%
                // Mastery: DCF >= 85% AND LPFF >= 85%
                const isFoundational = results.dcf < DCF_FOUNDATIONAL || results.lpff < LPFF_FOUNDATIONAL;
                const isMastery = results.dcf >= DCF_MASTERY && results.lpff >= LPFF_MASTERY;

                // Calculate extra value and stretch based on combined zone
                let extraValuePct, stretchPct;
                if (isFoundational) {
                  // Foundational zone: all stretch is potential, none captured
                  extraValuePct = 0;
                  stretchPct = STRETCH_POOL_PCT;
                } else if (isMastery) {
                  // Mastery zone: all stretch captured, none remaining
                  extraValuePct = STRETCH_POOL_PCT;
                  stretchPct = 0;
                } else {
                  // Stretch zone: use weighted combined factor (DCF 68.5%, LPFF 31.5%)
                  // DCF normalised: (dcf - 0.70) / 0.15, clamped 0-1
                  // LPFF normalised: (lpff - 0.50) / 0.35, clamped 0-1
                  const dcfNorm = Math.max(0, Math.min(1, (results.dcf - DCF_FOUNDATIONAL) / (DCF_MASTERY - DCF_FOUNDATIONAL)));
                  const lpffNorm = Math.max(0, Math.min(1, (results.lpff - LPFF_FOUNDATIONAL) / (LPFF_MASTERY - LPFF_FOUNDATIONAL)));
                  const combinedFactor = (dcfNorm * 0.685) + (lpffNorm * 0.315);

                  extraValuePct = STRETCH_POOL_PCT * combinedFactor;
                  stretchPct = STRETCH_POOL_PCT - extraValuePct;
                }

                // Calculate £ amounts
                const foundationalAmt = (FOUNDATIONAL_PCT / 100) * valueAtStake;
                const extraValueAmt = (extraValuePct / 100) * valueAtStake;
                const stretchAmt = (stretchPct / 100) * valueAtStake;
                const lostAmt = (PERMANENTLY_LOST_PCT / 100) * valueAtStake;

                // Calculate pie chart degrees
                const foundationalDeg = (FOUNDATIONAL_PCT / 100) * 360;
                const extraValueDeg = (extraValuePct / 100) * 360;
                const stretchDeg = (stretchPct / 100) * 360;

                // Build gradient stops
                let currentDeg = 0;
                const gradientStops = [];

                // Foundational (dark green)
                gradientStops.push(`#16a34a ${currentDeg}deg ${currentDeg + foundationalDeg}deg`);
                currentDeg += foundationalDeg;

                // Extra value you're gaining (lighter green) - only if > 0
                if (extraValuePct > 0.01) {
                  gradientStops.push(`#22c55e ${currentDeg}deg ${currentDeg + extraValueDeg}deg`);
                  currentDeg += extraValueDeg;
                }

                // Your stretch (light blue) - only if > 0
                if (stretchPct > 0.01) {
                  gradientStops.push(`#bae6fd ${currentDeg}deg ${currentDeg + stretchDeg}deg`);
                  currentDeg += stretchDeg;
                }

                // Permanently lost (red)
                gradientStops.push(`#dc2626 ${currentDeg}deg 360deg`);

                return (
                  <div className="flex items-center gap-5">
                    {/* Pie Chart */}
                    <div
                      className="w-[140px] h-[140px] rounded-full flex-shrink-0 relative shadow-md"
                      style={{
                        background: `conic-gradient(from 0deg, ${gradientStops.join(', ')})`
                      }}
                    >
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70px] h-[70px] bg-white rounded-full flex flex-col items-center justify-center shadow-inner">
                        <span className="text-[9px] text-daring-grey uppercase tracking-wide">Total</span>
                        <span className="text-base font-bold text-daring-navy">£{valueAtStake}M</span>
                      </div>
                    </div>
                    {/* Legend */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 py-1.5 border-b border-gray-100">
                        <div className="w-3 h-3 rounded-sm bg-green-600 flex-shrink-0"></div>
                        <span className="flex-1 text-xs text-daring-grey">Foundational value realised (57%)</span>
                        <span className="text-xs font-semibold text-daring-navy">{formatCurrency(foundationalAmt)}</span>
                      </div>
                      {extraValuePct > 0.01 && (
                        <div className="flex items-center gap-2 py-1.5 border-b border-gray-100">
                          <div className="w-3 h-3 rounded-sm bg-green-500 flex-shrink-0"></div>
                          <span className="flex-1 text-xs text-daring-grey">Extra value you're gaining ({extraValuePct.toFixed(1)}%)</span>
                          <span className="text-xs font-semibold text-daring-navy">{formatCurrency(extraValueAmt)}</span>
                        </div>
                      )}
                      {stretchPct > 0.01 && (
                        <div className="flex items-center gap-2 py-1.5 border-b border-gray-100">
                          <div className="w-3 h-3 rounded-sm bg-sky-200 flex-shrink-0"></div>
                          <span className="flex-1 text-xs text-daring-grey">Your stretch ({stretchPct.toFixed(1)}%)</span>
                          <span className="text-xs font-semibold text-daring-navy">{formatCurrency(stretchAmt)}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 py-1.5">
                        <div className="w-3 h-3 rounded-sm bg-red-600 flex-shrink-0"></div>
                        <span className="flex-1 text-xs text-daring-grey">Permanently lost (33%)</span>
                        <span className="text-xs font-semibold text-daring-navy">{formatCurrency(lostAmt)}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* 14 System Dependencies */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h3 className="text-sm font-bold text-daring-navy uppercase tracking-wide mb-4">
                14 System Dependencies
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {DEPENDENCY_LABELS.map((label, index) => {
                  const score = dependencyScores[`dep${index + 1}`];
                  const scoreClass = getScoreClass(score);
                  return (
                    <div
                      key={index}
                      className={`p-3 bg-gray-50 rounded-md border-l-4 ${
                        scoreClass === 'critical' ? 'border-red-500' :
                        scoreClass === 'weak' ? 'border-orange-500' :
                        scoreClass === 'moderate' ? 'border-yellow-500' :
                        scoreClass === 'strong' ? 'border-green-500' :
                        'border-emerald-500'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                          <span className="text-xs font-semibold text-daring-navy truncate">
                            {index + 1}. {label}
                          </span>
                          <div className="relative group flex-shrink-0">
                            <button
                              type="button"
                              className="w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-[10px] font-bold leading-none hover:bg-gray-300 transition-colors"
                              aria-label={`Info about ${label}`}
                            >
                              ?
                            </button>
                            <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-50 w-64 pointer-events-none">
                              <div className="bg-daring-navy text-white text-xs rounded-lg px-3 py-2 shadow-lg">
                                <div className="font-semibold mb-1">{label}</div>
                                <div className="text-gray-300 leading-relaxed">{DEPENDENCY_DESCRIPTIONS[index]}</div>
                                <div className="absolute top-full right-4 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-daring-navy"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <span
                          className={`text-sm font-bold ${
                            scoreClass === 'critical' ? 'text-red-500' :
                            scoreClass === 'weak' ? 'text-orange-500' :
                            scoreClass === 'moderate' ? 'text-yellow-600' :
                            scoreClass === 'strong' ? 'text-green-500' :
                            'text-emerald-500'
                          }`}
                        >
                          {score}
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        value={score}
                        onChange={(e) => handleDependencyChange(index + 1, e.target.value)}
                        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: 'linear-gradient(to right, #fecaca 0%, #fecaca 20%, #fed7aa 20%, #fed7aa 40%, #fef08a 40%, #fef08a 60%, #bbf7d0 60%, #bbf7d0 80%, #bfdbfe 80%, #bfdbfe 100%)'
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top 3 Dependencies */}
            {showTop3 && (
              <div className="bg-white rounded-lg shadow-sm p-5">
                <h3 className="text-sm font-bold text-daring-navy uppercase tracking-wide mb-4">
                  Top 3 Dependencies Requiring Attention
                </h3>
                <div className="space-y-4">
                  {top3Dependencies.map((item, index) => {
                    const scoreClass = getScoreClass(item.score);
                    return (
                      <div
                        key={item.id}
                        className={`p-4 rounded-md border-l-4 ${
                          scoreClass === 'critical' ? 'bg-red-50 border-red-500' :
                          scoreClass === 'weak' ? 'bg-orange-50 border-orange-500' :
                          scoreClass === 'moderate' ? 'bg-yellow-50 border-yellow-500' :
                          scoreClass === 'strong' ? 'bg-green-50 border-green-500' :
                          'bg-emerald-50 border-emerald-500'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-full bg-daring-navy text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start gap-2 mb-1">
                              <div className="text-sm font-semibold text-daring-navy">{item.fullLabel}</div>
                              <span className={`text-sm font-bold flex-shrink-0 ${
                                scoreClass === 'critical' ? 'text-red-500' :
                                scoreClass === 'weak' ? 'text-orange-500' :
                                scoreClass === 'moderate' ? 'text-yellow-600' :
                                scoreClass === 'strong' ? 'text-green-500' :
                                'text-emerald-500'
                              }`}>
                                {item.score}/10
                              </span>
                            </div>
                            <div className="text-xs text-daring-grey mb-2 italic">{item.summary}</div>
                            {item.impact && (
                              <div className="text-xs text-daring-navy leading-relaxed">
                                <span className="font-semibold">Impact:</span> {item.impact}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recommended Next Steps - System Burden */}
            {showNextSteps && (
              <div className="bg-white rounded-lg shadow-sm p-5">
                <h3 className="text-sm font-bold text-daring-navy uppercase tracking-wide mb-3">
                  Recommended Next Steps (Top 3 Dependencies)
                </h3>
                <p className="text-sm text-daring-grey mb-4">
                  Based on your data, here are evidence-based interventions to improve your system capability and recover transformation value:
                </p>
                <div className="space-y-4">
                  {top3Dependencies.map((item, index) => (
                    <div key={item.id} className="p-4 bg-sky-50 rounded-md border-l-4 border-sky-500">
                      <h4 className="text-sm font-semibold text-daring-navy mb-2">
                        {index + 1}. {item.actionSummary || item.label}
                      </h4>
                      <p className="text-xs text-daring-grey leading-relaxed">
                        {item.actionStatement || item.impact || 'No action statement available.'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: Leadership Burden */}
          <div className="flex flex-col gap-5">
            {/* Optimization by Intervention Type Pie Chart */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h3 className="text-sm font-bold text-daring-navy uppercase tracking-wide mb-4">
                Optimization by Intervention Type
              </h3>
              {(() => {
                // Use the stretch potential from v7.1 algorithm
                const stretchPotential = simplifiedResults.stretchPotential;
                const systemPortion = stretchPotential * SYSTEM_LOSS_WEIGHT;
                const leadershipPortion = stretchPotential * LEADERSHIP_LOSS_WEIGHT;

                return (
                  <div className="flex items-center gap-5">
                    {/* Pie Chart */}
                    <div
                      className="w-[140px] h-[140px] rounded-full flex-shrink-0 relative shadow-md"
                      style={{
                        background: stretchPotential > 0
                          ? `conic-gradient(
                              from 0deg,
                              #0EA5E9 0deg ${0.685 * 360}deg,
                              #8B5CF6 ${0.685 * 360}deg 360deg
                            )`
                          : '#16a34a' // Solid green when fully optimized
                      }}
                    >
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70px] h-[70px] bg-white rounded-full flex flex-col items-center justify-center shadow-inner">
                        <span className="text-[9px] text-daring-grey uppercase tracking-wide">{stretchPotential > 0 ? 'Stretch' : 'Optimized'}</span>
                        <span className="text-base font-bold text-daring-navy">{formatCurrency(stretchPotential)}</span>
                      </div>
                    </div>
                    {/* Legend */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 py-1.5 border-b border-gray-100">
                        <div className="w-3 h-3 rounded-sm bg-sky-500 flex-shrink-0"></div>
                        <span className="flex-1 text-xs text-daring-grey">System interventions</span>
                        <span className="text-xs font-semibold text-daring-navy">{formatCurrency(systemPortion)} (68.5%)</span>
                      </div>
                      <div className="flex items-center gap-2 py-1.5">
                        <div className="w-3 h-3 rounded-sm bg-violet-500 flex-shrink-0"></div>
                        <span className="flex-1 text-xs text-daring-grey">Leadership interventions</span>
                        <span className="text-xs font-semibold text-daring-navy">{formatCurrency(leadershipPortion)} (31.5%)</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* 10 Leadership Anxieties */}
            <div className="bg-white rounded-lg shadow-sm p-5">
              <h3 className="text-sm font-bold text-daring-navy uppercase tracking-wide mb-4">
                10 Leadership Anxieties
              </h3>
              <div className="grid grid-cols-2 gap-3" style={{ gridTemplateRows: 'repeat(7, minmax(0, 1fr))' }}>
                {ANXIETY_LABELS.map((label, index) => {
                  const score = anxietyScores[`anxiety${index + 1}`];
                  const scoreClass = getScoreClass(score);
                  return (
                    <div
                      key={index}
                      className={`p-3 bg-gray-50 rounded-md border-l-4 ${
                        scoreClass === 'critical' ? 'border-red-500' :
                        scoreClass === 'weak' ? 'border-orange-500' :
                        scoreClass === 'moderate' ? 'border-yellow-500' :
                        scoreClass === 'strong' ? 'border-green-500' :
                        'border-emerald-500'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                          <span className="text-xs font-semibold text-daring-navy truncate">
                            {index + 1}. {label}
                          </span>
                          <div className="relative group flex-shrink-0">
                            <button
                              type="button"
                              className="w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-[10px] font-bold leading-none hover:bg-gray-300 transition-colors"
                              aria-label={`Info about ${label}`}
                            >
                              ?
                            </button>
                            <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 w-64 pointer-events-none">
                              <div className="bg-daring-navy text-white text-xs rounded-lg px-3 py-2 shadow-lg">
                                <div className="font-semibold mb-1">{label}</div>
                                <div className="text-gray-300 leading-relaxed">{ANXIETY_DESCRIPTIONS[index]}</div>
                                <div className="absolute top-full left-4 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-daring-navy"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <span
                          className={`text-sm font-bold ${
                            scoreClass === 'critical' ? 'text-red-500' :
                            scoreClass === 'weak' ? 'text-orange-500' :
                            scoreClass === 'moderate' ? 'text-yellow-600' :
                            scoreClass === 'strong' ? 'text-green-500' :
                            'text-emerald-500'
                          }`}
                        >
                          {score}
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        value={score}
                        onChange={(e) => handleAnxietyChange(index + 1, e.target.value)}
                        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: 'linear-gradient(to right, #fecaca 0%, #fecaca 20%, #fed7aa 20%, #fed7aa 40%, #fef08a 40%, #fef08a 60%, #bbf7d0 60%, #bbf7d0 80%, #bfdbfe 80%, #bfdbfe 100%)'
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top 3 Anxieties */}
            {showTop3 && (
              <div className="bg-white rounded-lg shadow-sm p-5">
                <h3 className="text-sm font-bold text-daring-navy uppercase tracking-wide mb-4">
                  Top 3 Anxieties Requiring Attention
                </h3>
                <div className="space-y-4">
                  {top3Anxieties.map((item, index) => {
                    const scoreClass = getScoreClass(item.score);
                    return (
                      <div
                        key={item.id}
                        className={`p-4 rounded-md border-l-4 ${
                          scoreClass === 'critical' ? 'bg-red-50 border-red-500' :
                          scoreClass === 'weak' ? 'bg-orange-50 border-orange-500' :
                          scoreClass === 'moderate' ? 'bg-yellow-50 border-yellow-500' :
                          scoreClass === 'strong' ? 'bg-green-50 border-green-500' :
                          'bg-emerald-50 border-emerald-500'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-full bg-daring-navy text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {index + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start gap-2 mb-1">
                              <div className="text-sm font-semibold text-daring-navy">{item.fullLabel}</div>
                              <span className={`text-sm font-bold flex-shrink-0 ${
                                scoreClass === 'critical' ? 'text-red-500' :
                                scoreClass === 'weak' ? 'text-orange-500' :
                                scoreClass === 'moderate' ? 'text-yellow-600' :
                                scoreClass === 'strong' ? 'text-green-500' :
                                'text-emerald-500'
                              }`}>
                                {item.score}/10
                              </span>
                            </div>
                            <div className="text-xs text-daring-grey mb-2 italic">{item.summary}</div>
                            {item.impact && (
                              <div className="text-xs text-daring-navy leading-relaxed">
                                <span className="font-semibold">Impact:</span> {item.impact}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recommended Next Steps - Leadership Burden */}
            {showNextSteps && (
              <div className="bg-white rounded-lg shadow-sm p-5">
                <h3 className="text-sm font-bold text-daring-navy uppercase tracking-wide mb-3">
                  Recommended Next Steps (Top 3 Anxieties)
                </h3>
                <p className="text-sm text-daring-grey mb-4">
                  Based on your results, here are evidence-based interventions to improve leadership psychological flexibility and recover transformation value:
                </p>
                <div className="space-y-4">
                  {top3Anxieties.map((item, index) => (
                    <div key={item.id} className="p-4 bg-violet-50 rounded-md border-l-4 border-violet-500">
                      <h4 className="text-sm font-semibold text-daring-navy mb-2">
                        {index + 1}. {item.actionSummary || item.label}
                      </h4>
                      <p className="text-xs text-daring-grey leading-relaxed">
                        {item.actionStatement || item.impact || 'No action statement available.'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Scenario Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-bold text-daring-navy">Save Scenario</h3>
              <p className="text-sm text-daring-grey mt-1">
                Save current settings as a reusable scenario
              </p>
            </div>

            <div className="p-5 space-y-4">
              {/* Scenario Name */}
              <div>
                <label className="block text-sm font-semibold text-daring-navy mb-1">
                  Scenario Name *
                </label>
                <input
                  type="text"
                  value={newScenarioName}
                  onChange={(e) => setNewScenarioName(e.target.value)}
                  placeholder="e.g., Q1 2026 Baseline"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-daring-navy/50"
                  autoFocus
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-daring-navy mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={newScenarioDescription}
                  onChange={(e) => setNewScenarioDescription(e.target.value)}
                  placeholder="Brief description of this scenario..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-daring-navy/50 resize-none"
                />
              </div>

              {/* Leader-specific option */}
              {selectedLeaderId ? (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="saveForLeader"
                    checked={saveForLeader}
                    onChange={(e) => setSaveForLeader(e.target.checked)}
                    className="w-4 h-4 accent-daring-navy"
                  />
                  <label htmlFor="saveForLeader" className="text-sm text-daring-grey">
                    Save for selected leader only (
                    {leaders.find(l => l.id === parseInt(selectedLeaderId))?.firstName}{' '}
                    {leaders.find(l => l.id === parseInt(selectedLeaderId))?.lastName})
                  </label>
                </div>
              ) : (
                <div className="text-xs text-amber-600 bg-amber-50 rounded-md px-3 py-2">
                  No leader selected - scenario will be saved as a template
                </div>
              )}

              {/* Current settings preview */}
              <div className="bg-gray-50 rounded-md p-3 text-xs text-daring-grey">
                <div className="font-semibold text-daring-navy mb-1">Current Settings:</div>
                <div>Persona: {personaId === 'mark' ? 'Mark Ellison' : 'Sarah Lawson'}</div>
                <div>Value at Stake: £{valueAtStake}M</div>
                <div>DCF Zone: {results.dcfZone} ({formatPercent(results.dcf)})</div>
                <div>LPFF Zone: {results.lpffZone} ({formatPercent(results.lpff)})</div>
              </div>
            </div>

            <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setNewScenarioName('');
                  setNewScenarioDescription('');
                  setSaveForLeader(false);
                }}
                className="px-4 py-2 rounded-md text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveScenario}
                disabled={!newScenarioName.trim() || saving}
                className={`px-4 py-2 rounded-md text-sm font-semibold ${
                  newScenarioName.trim() && !saving
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {saving ? 'Saving...' : 'Save Scenario'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminDynamicResults;
